import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FileText,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Calendar,
  Building2,
  Briefcase,
  Download,
} from "lucide-react";
import { motion } from "framer-motion";
import useAuth from "../../../contexts/AuthContext";
import Loader from "../../../common_components/Loader";
import { useToast } from "../../../common_components/Toast";

interface MyApplicationsPageProps {
  type: "FACULTY" | "NON_FACULTY";
}

export default function MyApplicationsPage({ type }: MyApplicationsPageProps) {
  const { recruitmentId } = useParams();
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const { apiRequest } = useAuth();
  const navigate = useNavigate();
  const [recruitmentOver, setRecruitmentOver] = useState(true);
  const toast = useToast();
  const fetchedRef = useRef(false);

  const isFaculty = type === "FACULTY";
  const prefix = isFaculty ? "/faculty" : "/non-faculty";

  const fetchRecruitment = useCallback(async () => {
    try {
      const data = await apiRequest(`${prefix}/recruitment/${recruitmentId}`, {
        method: "GET",
      });
      if (data.recruitment) {
        const endDate = new Date(data?.recruitment?.applicationDates?.endDate);
        const now = new Date();
        if (now > endDate) {
          setRecruitmentOver(true);
        } else {
          setRecruitmentOver(false);
        }
      }
    } catch (error) {
      console.error("Error fetching recruitment:", error);
      toast.error("Error fetching recruitment details");
    }
  }, [apiRequest, prefix, recruitmentId, toast]);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const url = recruitmentId
        ? `${prefix}/application/my-applications?recruitmentId=${recruitmentId}`
        : `${prefix}/application/my-applications`;
      const res = await apiRequest(url, { method: "GET" });
      setApplications(res.applications || []);
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, prefix, recruitmentId]);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchApplications();
    if (recruitmentId) {
      fetchRecruitment();
    }
  }, [fetchApplications, fetchRecruitment, recruitmentId]);

  const getStatusConfig = (status: string) => {
    const configs: Record<string, any> = {
      DRAFT: {
        label: "Draft",
        icon: Edit,
        bgColor: "bg-gray-100",
        textColor: "text-gray-700",
      },
      SUBMITTED: {
        label: "Submitted",
        icon: CheckCircle,
        bgColor: "bg-blue-100",
        textColor: "text-blue-700",
      },
      ACCEPTED: {
        label: "Accepted",
        icon: CheckCircle,
        bgColor: "bg-green-100",
        textColor: "text-green-700",
      },
      REJECTED: {
        label: "Rejected",
        icon: XCircle,
        bgColor: "bg-red-100",
        textColor: "text-red-700",
      },
    };
    return configs[status] || configs.DRAFT;
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const filteredApplications = applications.filter((app: any) => {
    if (filter === "ALL") return true;
    return app.status === filter;
  });

  const stats = {
    total: applications.length,
    draft: applications.filter((a: any) => a.status === "DRAFT").length,
    submitted: applications.filter((a: any) => a.status === "SUBMITTED").length,
    accepted: applications.filter((a: any) => a.status === "ACCEPTED").length,
    rejected: applications.filter((a: any) => a.status === "REJECTED").length,
  };

  return (
    <div className="flex-grow bg-slate-50 font-sans rounded-xl p-2">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <StatCard
            label="Total"
            value={stats.total}
            color="blue"
            active={filter === "ALL"}
            onClick={() => setFilter("ALL")}
          />
          <StatCard
            label="Draft"
            value={stats.draft}
            color="gray"
            active={filter === "DRAFT"}
            onClick={() => setFilter("DRAFT")}
          />
          <StatCard
            label="Submitted"
            value={stats.submitted}
            color="blue"
            active={filter === "SUBMITTED"}
            onClick={() => setFilter("SUBMITTED")}
          />
          <StatCard
            label="Accepted"
            value={stats.accepted}
            color="green"
            active={filter === "ACCEPTED"}
            onClick={() => setFilter("ACCEPTED")}
          />
          <StatCard
            label="Rejected"
            value={stats.rejected}
            color="red"
            active={filter === "REJECTED"}
            onClick={() => setFilter("REJECTED")}
          />
        </div>

        {loading && <Loader />}

        {!loading && (
          <>
            {filteredApplications.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-16 border border-dashed border-slate-300 rounded-xl bg-white"
              >
                <div className="inline-block p-6 bg-slate-50 rounded-full mb-4 border border-slate-200">
                  <FileText className="w-16 h-16 text-slate-400" />
                </div>
                <h3 className="text-xl font-serif font-bold text-slate-700 mb-2">
                  {filter === "ALL"
                    ? "No Applications Yet"
                    : `No ${filter.toLowerCase().replace("_", " ")} applications`}
                </h3>
                <p className="text-slate-500 mb-6">
                  {filter === "ALL"
                    ? "Start by browsing available recruitments"
                    : "Try changing the filter to see other applications"}
                </p>
                {recruitmentId && (
                  <button
                    onClick={() => {
                      navigate(`${prefix}/application/${recruitmentId}/apply`);
                    }}
                    className="px-6 py-3 bg-slate-800 text-white rounded shadow hover:bg-slate-900 transition-all border border-slate-700 font-medium"
                  >
                    Apply Now
                  </button>
                )}
              </motion.div>
            ) : (
              <div className="space-y-4">
                {filteredApplications.map((application: any, index: number) => {
                  const statusConfig = getStatusConfig(application.status);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <motion.div
                      key={application._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden"
                    >
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-bold text-gray-900">
                                {application.recruitment?.title || "Recruitment"}
                              </h3>
                              <span
                                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}
                              >
                                <StatusIcon className="w-3 h-3" />
                                {statusConfig.label}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              Application ID:{" "}
                              <span className="font-medium">
                                {application.applicationId}
                              </span>
                            </p>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-3 gap-4 mb-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Building2 className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Department</p>
                              <p className="text-sm font-medium text-gray-900">
                                {application.personalInfo?.department || "N/A"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <Briefcase className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Position</p>
                              <p className="text-sm font-medium text-gray-900">
                                {application.personalInfo?.postName || "N/A"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <Calendar className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Applied On</p>
                              <p className="text-sm font-medium text-gray-900">
                                {application.submittedAt
                                  ? formatDate(application.submittedAt)
                                  : formatDate(application.createdAt)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {application.status === "DRAFT" && (
                          <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-medium text-gray-600">
                                Application Progress
                              </p>
                              <p className="text-xs font-medium text-blue-600">
                                Step {application.currentStep || 1} of 5
                              </p>
                            </div>
                            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{
                                  width: `${((application.currentStep || 1) / 5) * 100}%`,
                                }}
                                className="h-full bg-gradient-to-r from-blue-600 to-purple-600"
                              />
                            </div>
                          </div>
                        )}

                        {application.reviewComments && (
                          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="text-xs font-medium text-yellow-800 mb-1">
                              Review Comments:
                            </p>
                            <p className="text-sm text-yellow-700">
                              {application.reviewComments}
                            </p>
                          </div>
                        )}

                        <div className="flex items-center gap-3">
                          {application.status === "DRAFT" ? (
                            <button
                              onClick={() => {
                                navigate(
                                  `${prefix}/application/${application.recruitmentId}/${application.applicationId}/edit`
                                );
                              }}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg font-medium hover:bg-slate-900 transition-all"
                            >
                              <Edit className="w-4 h-4" />
                              Continue Application
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  navigate(
                                    `${prefix}/application/${application.recruitmentId}/${application.applicationId}/view`
                                  );
                                }}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 text-white rounded-lg font-medium hover:bg-slate-900 transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                                View Details
                              </button>
                              <button
                                onClick={() => {
                                  navigate(
                                    `${prefix}/application/${application.recruitmentId}/${application.applicationId}/print`
                                  );
                                }}
                                className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                              >
                                <Download className="w-4 h-4" />
                                Download
                              </button>
                            </>
                          )}
                        </div>
                      </div>

                      {application.status !== "DRAFT" && (
                        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <div>
                              <span className="font-medium">Created:</span>{" "}
                              {formatDate(application.createdAt)}
                            </div>
                            {application.submittedAt && (
                              <div>
                                <span className="font-medium">Submitted:</span>{" "}
                                {formatDate(application.submittedAt)}
                              </div>
                            )}
                            {application.updatedAt && (
                              <div>
                                <span className="font-medium">Last Updated:</span>{" "}
                                {formatDate(application.updatedAt)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
                {recruitmentId && !recruitmentOver && (
                  <div className="flex justify-end mt-10">
                    <button
                      onClick={() => {
                        navigate(`${prefix}/application/${recruitmentId}/apply`);
                      }}
                      className="flex items-center gap-2 px-5 py-2.5 bg-slate-800 text-white rounded-xl font-medium shadow-md hover:bg-slate-900 transition-all"
                    >
                      <Edit className="w-4 h-4" />
                      Start New Application
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: number;
  color: "blue" | "gray" | "yellow" | "green" | "red" | "purple";
  active: boolean;
  onClick: () => void;
}

function StatCard({ label, value, color, active, onClick }: StatCardProps) {
  const colorClasses = {
    blue: active ? "bg-blue-600 text-white" : "bg-white text-blue-700 hover:bg-blue-50",
    gray: active ? "bg-slate-800 text-white" : "bg-white text-slate-700 hover:bg-slate-50",
    yellow: active ? "bg-amber-600 text-white" : "bg-white text-amber-700 hover:bg-amber-50",
    green: active ? "bg-green-600 text-white" : "bg-white text-green-700 hover:bg-green-50",
    red: active ? "bg-red-600 text-white" : "bg-white text-red-700 hover:bg-red-50",
    purple: active ? "bg-purple-600 text-white" : "bg-white text-purple-700 hover:bg-purple-50",
  };

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`p-4 rounded-xl border border-slate-200 shadow-sm transition-all cursor-pointer ${colorClasses[color]}`}
    >
      <p className={`text-2xl font-bold mb-1 ${active ? "text-white" : ""}`}>{value}</p>
      <p className={`text-xs font-medium ${active ? "text-white/90" : ""}`}>{label}</p>
    </motion.button>
  );
}
